import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

interface Enrollment {
  id: number;
  course_id: string;
  course_title: string;
  status: string;
  points_awarded: number;
  created_at: string;
}

interface Favorite {
  id: number;
  course_id: string;
  created_at: string;
}

const courseIdToTitle: Record<string, string> = {
  "ion-anti-aging": "離子紅光逆齡抗衰老 課程",
  "ion-stretch-mark": "離子紅光去妊娠紋課程",
  "youthful-eyelid": "童顏眼瞼課程",
  "ai-postpartum": "高階 AI 全科產後課程",
};

const statusLabel: Record<string, string> = {
  pending: "待處理",
  confirmed: "已確認",
  completed: "已完成",
  cancelled: "已取消",
};

export default function Member() {
  const { user, profile, loading, signOut, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState("");

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth?redirect=/member");
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    setDataLoading(true);
    setDataError("");
    Promise.all([
      supabase
        .from("enrollments")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false }),
      supabase
        .from("favorites")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false }),
    ])
      .then(([enrollRes, favRes]) => {
        if (enrollRes.error || favRes.error) {
          setDataError("載入資料失敗，請稍後再試");
        } else {
          setEnrollments((enrollRes.data as Enrollment[]) || []);
          setFavorites((favRes.data as Favorite[]) || []);
        }
      })
      .catch(() => setDataError("載入資料失敗，請稍後再試"))
      .finally(() => setDataLoading(false));
  }, [user]);

  const removeFavorite = async (id: number) => {
    const { error } = await supabase.from("favorites").delete().eq("id", id);
    if (!error) {
      setFavorites((prev) => prev.filter((f) => f.id !== id));
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-50/60 flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-brand-600 text-4xl"></i>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const levelColor = profile?.level === "金級會員" ? "text-amber-600" : profile?.level === "銀級會員" ? "text-slate-500" : "text-brand-600";

  return (
    <div className="min-h-screen bg-brand-50/60 text-brand-900">
      <Navbar />

      <section className="pt-32 md:pt-40 pb-16 md:pb-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <div className="inline-block px-6 py-2 rounded-md bg-brand-600 text-white text-lg tracking-wider">會員中心</div>
              <h1 className="mt-4 font-serif text-3xl md:text-4xl text-brand-800">
                你好，{profile?.name || user.email}
              </h1>
            </div>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-md border border-brand-300 text-brand-700 hover:bg-brand-50 text-sm whitespace-nowrap cursor-pointer transition"
            >
              <i className="ri-logout-box-r-line"></i> 登出
            </button>
          </div>

          {/* 會員資訊卡 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            <div className="bg-white border border-brand-100 rounded-lg p-5">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-100 text-brand-700">
                  <i className="ri-vip-crown-line text-xl"></i>
                </span>
                <div>
                  <p className="text-xs text-brand-700/60">會員等級</p>
                  <p className={`text-lg font-semibold ${levelColor}`}>{profile?.level || "普通會員"}</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-brand-100 rounded-lg p-5">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-100 text-brand-700">
                  <i className="ri-star-line text-xl"></i>
                </span>
                <div>
                  <p className="text-xs text-brand-700/60">累積點數</p>
                  <p className="text-lg font-semibold text-brand-700">{profile?.points ?? 0} 點</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-brand-100 rounded-lg p-5">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-100 text-brand-700">
                  <i className="ri-mail-line text-xl"></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-brand-700/60">會員電郵</p>
                  <p className="text-sm font-medium text-brand-900 truncate">{user.email}</p>
                </div>
              </div>
            </div>
          </div>

          {dataError && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 mb-6 flex items-center justify-between">
              <p className="text-sm text-rose-600">{dataError}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-3 py-1.5 rounded-md bg-rose-600 text-white text-xs whitespace-nowrap cursor-pointer"
              >
                重試
              </button>
            </div>
          )}

          {/* 報名紀錄 */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl text-brand-800 mb-4">我的報名紀錄</h2>
            {dataLoading ? (
              <div className="bg-white border border-brand-100 rounded-lg p-10 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-brand-500 text-2xl"></i>
              </div>
            ) : enrollments.length === 0 ? (
              <div className="bg-white border border-brand-100 rounded-lg p-10 text-center">
                <i className="ri-file-list-3-line text-4xl text-brand-300"></i>
                <p className="mt-3 text-sm text-brand-700/60">你還沒有任何報名紀錄</p>
                <Link
                  to="/courses"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm whitespace-nowrap cursor-pointer transition"
                >
                  瀏覽課程
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {enrollments.map((e) => (
                  <div key={e.id} className="bg-white border border-brand-100 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-md bg-brand-100 text-brand-700">
                        <i className="ri-book-open-line"></i>
                      </span>
                      <div>
                        <p className="font-medium text-brand-900">{e.course_title}</p>
                        <p className="text-xs text-brand-700/60 mt-1">
                          報名日期：{new Date(e.created_at).toLocaleDateString("zh-HK")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs whitespace-nowrap">
                        {statusLabel[e.status] || e.status}
                      </span>
                      <span className="text-xs text-brand-700/60 whitespace-nowrap">+{e.points_awarded} 點</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* 收藏清單 */}
          <section>
            <h2 className="font-serif text-2xl text-brand-800 mb-4">我的收藏</h2>
            {dataLoading ? (
              <div className="bg-white border border-brand-100 rounded-lg p-10 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-brand-500 text-2xl"></i>
              </div>
            ) : favorites.length === 0 ? (
              <div className="bg-white border border-brand-100 rounded-lg p-10 text-center">
                <i className="ri-heart-3-line text-4xl text-brand-300"></i>
                <p className="mt-3 text-sm text-brand-700/60">你還沒有收藏任何課程</p>
                <Link
                  to="/courses"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm whitespace-nowrap cursor-pointer transition"
                >
                  去收藏課程
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {favorites.map((f) => (
                  <div key={f.id} className="bg-white border border-brand-100 rounded-lg p-5 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-10 h-10 flex items-center justify-center rounded-md bg-brand-100 text-brand-700">
                        <i className="ri-bookmark-line"></i>
                      </span>
                      <p className="text-sm font-medium text-brand-900 truncate">
                        {courseIdToTitle[f.course_id] || f.course_id}
                      </p>
                    </div>
                    <button
                      onClick={() => removeFavorite(f.id)}
                      className="w-9 h-9 flex items-center justify-center rounded-full text-brand-500 hover:bg-brand-50 cursor-pointer transition"
                      aria-label="移除收藏"
                    >
                      <i className="ri-heart-fill"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </section>

      <Footer />
    </div>
  );
}